/**
 * bundle-builder.js
 * Hideaway Bundle Builder — vanilla JS, no vendor CDN, no framework.
 *
 * Responsibilities:
 *  - Selection state management (steps 1 + 2, per-product qty, min/max + selection_total)
 *  - Multi-box support (Box property increments per "Add another box")
 *  - Add-on tier UI + optional selection
 *  - Upsell block toggle
 *  - GAMIFIED "build your box" experience: a slot tray that fills as you pick,
 *    a live savings count-up, escalating milestone messaging, and a completion
 *    celebration. Injected at runtime so both the landing section and the PDP
 *    modal get it with zero duplicated markup.
 *  - Sticky summary update (desktop sidebar + mobile footer bar)
 *  - Single /cart/add.js call with `items` array; EXACT line property keys per §6
 *  - aria-live selection counter announcement
 *
 * LINE PROPERTY CONTRACT (§6 — do not rename):
 *  Every component line:
 *    { "Box": "<n>", "_bundleName": "<title>", "_easyBundle:prodQty": "1",
 *      "_easyBundle:OfferId": "<offer_id>" }
 *  Add-on lines additionally:
 *    { "_addon_offer_id": "<offer_id>", "_addon_product": "true",
 *      "_addonTierId": "<tier_id>" }
 *  Upsell line additionally:
 *    { "_bundleUpsell": "true" }   ← excluded from the bundle group by the
 *                                     pricing function (full price, no discount)
 */
(function () {
  'use strict';

  // ─── Helpers ─────────────────────────────────────────────────────────────

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Escape HTML to prevent XSS when building snippets from data. */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Format a Shopify money integer (cents) as "$X.XX" (drops trailing .00). */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /** Post JSON to Shopify's Cart AJAX API */
  async function cartAdd(items) {
    const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ description: 'Unknown error' }));
      throw new Error(err.description || 'Cart error');
    }
    return res.json();
  }

  // ─── BundleBuilder class ─────────────────────────────────────────────────

  class BundleBuilder {
    constructor(root) {
      this.root = root;

      // Data from liquid data attributes (set by section template)
      this.bundleTitle  = root.dataset.bundleTitle  || '';
      this.offerId      = root.dataset.offerId      || '';
      this.bundlePrice  = parseInt(root.dataset.bundlePrice  || '0', 10); // cents
      this.totalPrice   = parseInt(root.dataset.totalPrice   || '0', 10); // cents (Σ component retail — fallback only)
      this.selectionTotal = parseInt(root.dataset.selectionTotal || '0', 10);
      this.boxNumber    = 1;  // current box; increments on "Add another box"

      // State: Map<variantId, qty>  (one map covers both steps)
      this.selection    = new Map();
      this.addonSel     = new Map(); // Map<variantId, { tierId, qty }>
      this.upsellSel    = false;

      // Gamification runtime state
      this._wasComplete   = false;
      this._shownSavings  = 0;   // currently displayed savings (cents) for count-up
      this._savingsRaf    = null;

      this._bindElements();
      this._buildGameBoard();
      this._bindEvents();
      this._applyPreselect();
      this.render();
    }

    /**
     * Seamless PDP entry: when the root carries data-preselect-product (set by
     * bundle-upsell-card.liquid on single-perfume PDPs), start with that scent
     * already in slot 1. Silently no-ops if the product has no tile here or is
     * sold out — the builder just opens empty.
     */
    _applyPreselect() {
      const pid = this.root.dataset.preselectProduct;
      if (!pid) return;
      const tile = this.root.querySelector(
        `.bb-tile[data-product-id="${CSS.escape(pid)}"]:not(.bb-tile--addon)`
      );
      if (!tile || tile.dataset.available === 'false') return;
      const vid = tile.dataset.variantId;
      if (!vid) return;
      this.selection.set(vid, 1);
      this._preselectVid  = vid;
      this._preselectName = tile.dataset.productTitle || '';
    }

    // ── DOM references ───────────────────────────────────────────────────

    _bindElements() {
      const q  = (sel) => this.root.querySelector(sel);
      const qq = (sel) => Array.from(this.root.querySelectorAll(sel));
      // In the PDP modal the footer bar lives in .bb-modal__footer, OUTSIDE the
      // builder root (so it stays pinned below the scrollable body). Fall back
      // to the enclosing modal when a footer element isn't inside the root.
      const modal = this.root.closest('.bb-modal');
      const qf = (sel) => q(sel) || (modal ? modal.querySelector(sel) : null);

      this.tiles       = qq('.bb-tile[data-variant-id]:not(.bb-tile--addon)');
      this.addonTiles  = qq('.bb-tile--addon[data-variant-id]');
      this.upsellBtn   = q('.bb__upsell-btn');

      // Summary sidebar
      this.summaryItems       = q('.bb__summary-items');
      this.summaryProgressEl  = q('.bb__summary-progress');
      this.summaryProgressLbl = q('.bb__summary-progress-label');
      this.summaryPriceEl     = q('.bb__summary-price-value');
      this.summarySavingsEl   = q('.bb__summary-savings');
      this.summaryCtaBtn      = q('.bb__summary-cta');

      // Footer bar (modal-scoped fallback)
      this.footerCounter  = qf('.bb__footer-counter');
      this.footerPrice    = qf('.bb__footer-price');
      this.footerSavings  = qf('.bb__footer-savings');
      this.footerCtaBtn   = qf('.bb__footer-cta');

      // Step counter els
      this.stepCounters = qq('[data-step-counter]');

      // Aria-live announcement region
      this.liveRegion = q('.bb__counter-live');

      // Toast
      this.toast = q('.bb__toast') || document.getElementById('bb-toast');

      // Add another box button
      this.addAnotherBtn = q('.bb__add-another');
    }

    // ── Game board (injected) ─────────────────────────────────────────────

    /**
     * Build the gamified "your box" board and mount it at the top of the
     * builder root. Created in JS so the landing section and the PDP modal
     * share it without duplicating liquid markup.
     */
    _buildGameBoard() {
      if (this.selectionTotal <= 0) return;

      const board = document.createElement('div');
      board.className = 'bb__board';
      board.setAttribute('data-complete', 'false');

      const slots = [];
      for (let i = 0; i < this.selectionTotal; i++) {
        slots.push(
          `<li class="bb__slot" data-filled="false" aria-hidden="true">` +
            `<span class="bb__slot-num">${i + 1}</span>` +
          `</li>`
        );
      }

      board.innerHTML =
        '<div class="bb__board-head">' +
          '<p class="bb__board-eyebrow">Curate your set</p>' +
          '<p class="bb__board-milestone" role="status" aria-live="polite"></p>' +
        '</div>' +
        `<ul class="bb__board-slots" aria-hidden="true">${slots.join('')}</ul>` +
        '<div class="bb__board-meta">' +
          '<div class="bb__board-progress" aria-hidden="true">' +
            '<span class="bb__board-progress-fill"></span>' +
          '</div>' +
          '<p class="bb__board-savings" hidden>' +
            '<span class="bb__board-savings-spark" aria-hidden="true">✦</span> ' +
            'You save <strong class="bb__board-savings-amt">$0</strong>' +
          '</p>' +
        '</div>' +
        // Designer-value reveal: aspirational "the originals cost this much".
        // Populated only when selected items carry a designer_rrp metafield.
        '<p class="bb__board-rrp" role="status" aria-live="polite" hidden>' +
          '<span class="bb__board-rrp-label">Designer originals would cost</span> ' +
          '<s class="bb__board-rrp-orig">$0</s>' +
          '<span class="bb__board-rrp-pct"></span>' +
        '</p>' +
        '<div class="bb__burst" aria-hidden="true"></div>';

      // Mount as the first child of the builder root, above the layout.
      this.root.insertBefore(board, this.root.firstChild);

      this.board          = board;
      this.boardSlots     = Array.from(board.querySelectorAll('.bb__slot'));
      this.boardMilestone = board.querySelector('.bb__board-milestone');
      this.boardProgress  = board.querySelector('.bb__board-progress-fill');
      this.boardSavings   = board.querySelector('.bb__board-savings');
      this.boardSavingsAmt= board.querySelector('.bb__board-savings-amt');
      this.boardRrp       = board.querySelector('.bb__board-rrp');
      this.boardRrpOrig   = board.querySelector('.bb__board-rrp-orig');
      this.boardRrpPct    = board.querySelector('.bb__board-rrp-pct');
      this.burst          = board.querySelector('.bb__burst');
    }

    // ── Event wiring ─────────────────────────────────────────────────────

    _bindEvents() {
      // Tile toggle: delegate from root
      this.root.addEventListener('click', (e) => {
        const tile = e.target.closest('.bb-tile[data-variant-id]');
        if (!tile) return;

        // If click landed on a qty button, let that handler fire instead
        if (e.target.closest('.bb-tile__qty-btn')) return;

        this._toggleTile(tile);
      });

      // Qty +/- buttons
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest('.bb-tile__qty-btn');
        if (!btn) return;
        e.stopPropagation();
        const tile = btn.closest('.bb-tile[data-variant-id]');
        if (!tile) return;
        const delta = btn.dataset.delta === '-1' ? -1 : 1;
        this._changeQty(tile, delta);
      });

      // Keyboard: space/enter on tile activates toggle
      this.root.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        const tile = e.target.closest('.bb-tile[data-variant-id]');
        if (!tile || e.target.closest('.bb-tile__qty-btn')) return;
        e.preventDefault();
        this._toggleTile(tile);
      });

      // Upsell toggle
      if (this.upsellBtn) {
        this.upsellBtn.addEventListener('click', () => {
          this.upsellSel = !this.upsellSel;
          this.upsellBtn.setAttribute('aria-pressed', String(this.upsellSel));
          this.render();
        });
      }

      // CTA buttons
      [this.summaryCtaBtn, this.footerCtaBtn].forEach((btn) => {
        if (!btn) return;
        btn.addEventListener('click', () => this._addToCart());
      });

      // Add another box
      if (this.addAnotherBtn) {
        this.addAnotherBtn.addEventListener('click', () => this._addAnotherBox());
      }
    }

    // ── Selection logic ──────────────────────────────────────────────────

    _tileFor(vid) {
      return this.root.querySelector(`.bb-tile[data-variant-id="${CSS.escape(vid)}"]:not(.bb-tile--addon)`);
    }

    _toggleTile(tile) {
      // Sold-out variants would 422 on /cart/add.js — never let them be picked.
      if (tile.dataset.available === 'false') {
        this._showToast('Sorry, that one is sold out');
        return;
      }
      const vid  = tile.dataset.variantId;
      const isAddon = tile.classList.contains('bb-tile--addon');

      if (isAddon) {
        const tierId = tile.dataset.tierId || '';
        if (this.addonSel.has(vid)) {
          this.addonSel.delete(vid);
        } else {
          this.addonSel.set(vid, { tierId, qty: 1 });
        }
      } else {
        if (this.selection.has(vid)) {
          this.selection.delete(vid);
        } else {
          const total = this._selectedCount();
          if (total >= this.selectionTotal) {
            this._showToast(`Your box holds ${this.selectionTotal} — remove one to swap`);
            this._nudgeBoard();
            return;
          }
          this.selection.set(vid, 1);
          this._pulseTile(tile);
        }
      }
      this.render();
    }

    _changeQty(tile, delta) {
      if (tile.dataset.available === 'false') return;
      const vid = tile.dataset.variantId;
      const isAddon = tile.classList.contains('bb-tile--addon');

      if (isAddon) {
        const cur = this.addonSel.get(vid);
        if (!cur) return;
        const next = Math.max(0, (cur.qty || 1) + delta);
        if (next === 0) {
          this.addonSel.delete(vid);
        } else {
          this.addonSel.set(vid, { ...cur, qty: next });
        }
      } else {
        const cur = this.selection.get(vid) || 0;
        const total = this._selectedCount();

        if (delta > 0 && total >= this.selectionTotal) {
          this._showToast(`Your box is full at ${this.selectionTotal}`);
          this._nudgeBoard();
          return;
        }
        const next = Math.max(0, cur + delta);
        if (next === 0) {
          this.selection.delete(vid);
        } else {
          this.selection.set(vid, next);
          if (delta > 0) this._pulseTile(tile);
        }
      }
      this.render();
    }

    _selectedCount() {
      let n = 0;
      for (const qty of this.selection.values()) n += qty;
      return n;
    }

    _isComplete() {
      return this._selectedCount() >= this.selectionTotal;
    }

    /** Ordered list of selected units (one entry per unit, qty-expanded). */
    _selectedUnits() {
      const units = [];
      this.selection.forEach((qty, vid) => {
        const tile = this._tileFor(vid);
        const img  = tile ? tile.dataset.productImage || '' : '';
        const name = tile ? tile.dataset.productTitle || '' : '';
        for (let i = 0; i < qty; i++) units.push({ vid, img, name });
      });
      return units;
    }

    /** Live retail value (cents) of the selected component items. */
    _selectedRetailCents() {
      let c = 0;
      this.selection.forEach((qty, vid) => {
        const tile = this._tileFor(vid);
        const price = tile ? parseInt(tile.dataset.price || '0', 10) : 0;
        c += price * qty;
      });
      return c;
    }

    /** Live savings (cents) = selected retail − fixed bundle price (floored at 0). */
    _savingsCents() {
      const retail = this._selectedRetailCents();
      // No items picked yet → no savings to show (the pill stays hidden).
      if (retail <= 0) return 0;
      return Math.max(0, retail - this.bundlePrice);
    }

    /**
     * Live designer RRP (cents) of the selected component items — the combined
     * price of the original fragrances these dupes are inspired by. Sums only
     * tiles that carry a data-rrp (from the designer_rrp metafield); items
     * without it simply contribute nothing. Display-only.
     */
    _selectedRrpCents() {
      let c = 0;
      this.selection.forEach((qty, vid) => {
        const tile = this._tileFor(vid);
        const rrp = tile ? parseInt(tile.dataset.rrp || '0', 10) : 0;
        c += rrp * qty;
      });
      return c;
    }

    // ── Render ───────────────────────────────────────────────────────────

    render() {
      const count    = this._selectedCount();
      const total    = this.selectionTotal;
      const complete = count >= total;
      const maxReached = count >= total;

      // Tile states (main steps)
      this.tiles.forEach((tile) => {
        const vid = tile.dataset.variantId;
        const qty = this.selection.get(vid) || 0;
        const selected = qty > 0;
        const disabled = !selected && maxReached;

        tile.dataset.selected = String(selected);
        tile.dataset.disabled = String(disabled);
        tile.setAttribute('aria-pressed', String(selected));

        // Qty display
        const qtyVal = tile.querySelector('.bb-tile__qty-val');
        if (qtyVal) qtyVal.textContent = qty;

        // +/- button states
        const minusBtn = tile.querySelector('[data-delta="-1"]');
        const plusBtn  = tile.querySelector('[data-delta="1"]');
        if (minusBtn) minusBtn.disabled = qty <= 0;
        if (plusBtn)  plusBtn.disabled  = maxReached && !selected;
      });

      // Addon tile states
      this.addonTiles.forEach((tile) => {
        const vid = tile.dataset.variantId;
        const entry = this.addonSel.get(vid);
        const qty = entry ? (entry.qty || 1) : 0;
        const selected = qty > 0;

        tile.dataset.selected = String(selected);
        tile.setAttribute('aria-pressed', String(selected));

        const qtyVal = tile.querySelector('.bb-tile__qty-val');
        if (qtyVal) qtyVal.textContent = qty;

        const minusBtn = tile.querySelector('[data-delta="-1"]');
        if (minusBtn) minusBtn.disabled = qty <= 1;
      });

      // Step counters
      this.stepCounters.forEach((el) => {
        const step = el.dataset.stepCounter;
        if (step === 'total') {
          el.textContent = `${count} of ${total} chosen`;
          el.dataset.complete = String(complete);
        }
      });

      // Aria-live announcement
      if (this.liveRegion) {
        this.liveRegion.textContent = complete
          ? `Box complete — ${count} of ${total} selected.`
          : `${count} of ${total} selected.`;
      }

      // Game board + summary
      this._renderBoard(count, total, complete);
      this._renderSummary(count, total, complete);

      // Completion celebration (edge-triggered)
      if (complete && !this._wasComplete) {
        this._celebrate();
      }
      this._wasComplete = complete;
    }

    _renderBoard(count, total, complete) {
      if (!this.board) return;

      const units = this._selectedUnits();
      this.boardSlots.forEach((slot, i) => {
        const unit = units[i];
        if (unit) {
          // Re-render when the slot becomes filled OR when units shifted and
          // this slot now represents a different item (e.g. a middle item was
          // removed), so the thumbnail always matches the current selection.
          if (slot.dataset.filled !== 'true' || slot.dataset.vid !== unit.vid) {
            slot.dataset.filled = 'true';
            slot.dataset.vid = unit.vid;
            const safeName = esc(unit.name);
            slot.innerHTML = unit.img
              ? `<img class="bb__slot-img" src="${esc(unit.img)}" alt="${safeName}" loading="lazy">`
              : `<span class="bb__slot-fill" aria-hidden="true"></span>`;
          }
        } else if (slot.dataset.filled !== 'false') {
          slot.dataset.filled = 'false';
          slot.dataset.vid = '';
          slot.innerHTML = `<span class="bb__slot-num">${i + 1}</span>`;
        }
      });

      // Progress fill
      const pct = total > 0 ? Math.min(100, Math.round((count / total) * 100)) : 0;
      if (this.boardProgress) this.boardProgress.style.width = pct + '%';

      // Milestone messaging
      if (this.boardMilestone) {
        let msg;
        const remaining = total - count;
        if (count === 0) {
          msg = 'Tap items to start building your box';
        } else if (complete) {
          const sv = this._savingsCents();
          msg = sv > 0 ? `Box complete — you saved ${money(sv)}!` : 'Box complete — ready to go!';
        } else if (remaining === 1) {
          msg = 'One more to unlock your price';
        } else if (
          this._preselectName &&
          this.selection.size === 1 &&
          this.selection.has(this._preselectVid)
        ) {
          // Fresh from the PDP upsell — acknowledge the scent they came with.
          msg = `${this._preselectName} is in — pick ${remaining} more`;
        } else {
          msg = `${remaining} more to complete your set`;
        }
        this.boardMilestone.textContent = msg;
      }

      this.board.dataset.complete = String(complete);

      // Live savings
      const savings = this._savingsCents();
      if (this.boardSavings) {
        if (savings > 0) {
          this.boardSavings.hidden = false;
          this._animateSavings(savings);
        } else {
          this.boardSavings.hidden = true;
          this._shownSavings = 0;
        }
      }

      // Designer-value reveal: "the originals would cost $X (you save Y%)".
      // Shown only when selected items carry designer RRP data and that total
      // beats the bundle price — i.e. when the comparison is genuinely fun.
      if (this.boardRrp) {
        const rrp = this._selectedRrpCents();
        if (count > 0 && rrp > this.bundlePrice) {
          const pct = Math.round((1 - this.bundlePrice / rrp) * 100);
          this.boardRrpOrig.textContent = money(rrp);
          this.boardRrpPct.textContent = pct > 0 ? ` — you save ${pct}%` : '';
          this.boardRrp.hidden = false;
        } else {
          this.boardRrp.hidden = true;
        }
      }
    }

    /** Animated count-up for the savings figure (instant when reduced motion). */
    _animateSavings(targetCents) {
      if (!this.boardSavingsAmt) return;
      const from = this._shownSavings;
      if (prefersReducedMotion || from === targetCents) {
        this._shownSavings = targetCents;
        this.boardSavingsAmt.textContent = money(targetCents);
        return;
      }
      if (this._savingsRaf) cancelAnimationFrame(this._savingsRaf);

      const duration = 450;
      let start = null;
      const step = (ts) => {
        if (start === null) start = ts;
        const t = Math.min(1, (ts - start) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        const cur = Math.round(from + (targetCents - from) * eased);
        // Track the displayed value every frame so an animation that starts
        // mid-flight eases from what's on screen, not a stale baseline.
        this._shownSavings = cur;
        this.boardSavingsAmt.textContent = money(cur);
        if (t < 1) {
          this._savingsRaf = requestAnimationFrame(step);
        } else {
          this._shownSavings = targetCents;
        }
      };
      this._savingsRaf = requestAnimationFrame(step);
    }

    _renderSummary(count, total, complete) {
      const savings = this._savingsCents();
      const priceTxt = money(this.bundlePrice);
      const savingsTxt = savings > 0 ? `You save ${money(savings)}` : '';
      const pct = Math.min(100, total > 0 ? Math.round((count / total) * 100) : 0);

      const ctaLabel = complete
        ? `Add to cart · ${priceTxt}`
        : count === 0
        ? `Pick your ${total} items`
        : `Choose ${total - count} more`;

      // Sidebar
      if (this.summaryProgressEl) {
        this.summaryProgressEl.value = pct;
        this.summaryProgressEl.max   = 100;
        this.summaryProgressEl.setAttribute('aria-valuenow', String(pct));
      }
      if (this.summaryProgressLbl) {
        this.summaryProgressLbl.textContent = `${count}/${total} selected`;
      }
      if (this.summaryPriceEl) {
        this.summaryPriceEl.textContent = priceTxt;
      }
      if (this.summarySavingsEl) {
        this.summarySavingsEl.textContent = savingsTxt;
        this.summarySavingsEl.hidden = savings === 0;
      }
      if (this.summaryCtaBtn) {
        this.summaryCtaBtn.textContent = ctaLabel;
        this.summaryCtaBtn.disabled = !complete;
        this.summaryCtaBtn.dataset.ready = String(complete);
      }
      if (this.summaryItems) {
        this.summaryItems.innerHTML = this._buildSummaryItemsHTML();
      }

      // Footer bar
      if (this.footerCounter) {
        this.footerCounter.textContent = `${count}/${total} chosen`;
      }
      if (this.footerPrice) {
        this.footerPrice.textContent = priceTxt;
      }
      if (this.footerSavings) {
        this.footerSavings.textContent = savingsTxt;
        this.footerSavings.hidden = savings === 0;
      }
      if (this.footerCtaBtn) {
        this.footerCtaBtn.textContent = ctaLabel;
        this.footerCtaBtn.disabled = !complete;
        this.footerCtaBtn.dataset.ready = String(complete);
      }
    }

    _buildSummaryItemsHTML() {
      if (this.selection.size === 0) {
        return '<li class="bb__summary-empty">No items selected yet</li>';
      }
      const lines = [];
      this.selection.forEach((qty, vid) => {
        const tile = this._tileFor(vid);
        if (!tile) return;
        const name = tile.dataset.productTitle || vid;
        const img  = tile.dataset.productImage || '';
        lines.push(`
          <li class="bb__summary-item">
            ${img ? `<img class="bb__summary-item-img" src="${esc(img)}" alt="${esc(name)}" loading="lazy">` : ''}
            <span class="bb__summary-item-name">${esc(name)}</span>
            ${qty > 1 ? `<span class="bb__summary-item-qty">×${qty}</span>` : ''}
          </li>`);
      });
      return lines.join('');
    }

    // ── Micro-interactions ────────────────────────────────────────────────

    _pulseTile(tile) {
      if (prefersReducedMotion || !tile) return;
      tile.classList.remove('bb-tile--pop');
      // Force reflow so the animation restarts on rapid re-selection.
      void tile.offsetWidth;
      tile.classList.add('bb-tile--pop');
    }

    _nudgeBoard() {
      if (prefersReducedMotion || !this.board) return;
      this.board.classList.remove('bb__board--nudge');
      void this.board.offsetWidth;
      this.board.classList.add('bb__board--nudge');
    }

    /** Confetti-free sparkle burst on box completion. Respects reduced motion. */
    _celebrate() {
      if (prefersReducedMotion || !this.burst) return;
      const COUNT = 14;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < COUNT; i++) {
        const s = document.createElement('span');
        s.className = 'bb__spark';
        const angle = (Math.PI * 2 * i) / COUNT + (i % 2 ? 0.3 : 0);
        const dist = 46 + (i % 4) * 14;
        s.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
        s.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
        s.style.setProperty('--d', 60 * (i % 5) + 'ms');
        frag.appendChild(s);
      }
      this.burst.innerHTML = '';
      this.burst.appendChild(frag);
      // Clean up after the longest spark finishes.
      clearTimeout(this._burstTimer);
      this._burstTimer = setTimeout(() => { if (this.burst) this.burst.innerHTML = ''; }, 1200);
    }

    // ── Multi-box ────────────────────────────────────────────────────────

    _addAnotherBox() {
      if (!this._isComplete()) {
        this._showToast(`Complete box ${this.boxNumber} first`);
        this._nudgeBoard();
        return;
      }
      // Submit current box first, then reset for box n+1
      this._addToCart({ andReset: true });
    }

    _resetBoard() {
      this._wasComplete = false;
      this._shownSavings = 0;
      this.boardSlots.forEach((slot, i) => {
        slot.dataset.filled = 'false';
        slot.dataset.vid = '';
        slot.innerHTML = `<span class="bb__slot-num">${i + 1}</span>`;
      });
    }

    // ── Cart submission ──────────────────────────────────────────────────

    async _addToCart({ andReset = false } = {}) {
      if (!this._isComplete()) return;

      const btn1 = this.summaryCtaBtn;
      const btn2 = this.footerCtaBtn;

      // Loading state
      [btn1, btn2].forEach((b) => {
        if (!b) return;
        b.disabled = true;
        b.dataset.origLabel = b.textContent;
        b.innerHTML = '<span class="bb__spinner" aria-hidden="true"></span>Adding…';
      });

      try {
        const items = this._buildCartItems();
        await cartAdd(items);

        if (andReset) {
          this.boxNumber += 1;
          this.selection.clear();
          this.addonSel.clear();
          this.upsellSel = false;
          if (this.board) this._resetBoard();
          this.render();
          this._showToast(`Box ${this.boxNumber - 1} added! Start box ${this.boxNumber}`);
        } else {
          this._showToast('Added to cart!');
          // Dispatch a cart:updated event so UpCart drawer re-opens
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } catch (err) {
        this._showToast(err.message || 'Could not add to cart');
        console.error('[bundle-builder]', err);
      } finally {
        [btn1, btn2].forEach((b) => {
          if (!b) return;
          b.textContent = b.dataset.origLabel || '';
          delete b.dataset.origLabel;
          b.disabled = !this._isComplete();
        });
      }
    }

    /**
     * Build the `items` array for /cart/add.js.
     *
     * LINE PROPERTY CONTRACT (§6 — exact keys, do not rename):
     *   Every component line:
     *     Box              → box number (string)
     *     _bundleName      → bundle metaobject title
     *     _easyBundle:prodQty → "1" (always — EB anatomy)
     *     _easyBundle:OfferId → offerId from the bundle metaobject
     *   Add-on lines additionally:
     *     _addon_offer_id  → same offerId
     *     _addon_product   → "true"
     *     _addonTierId     → tier_id from the bundle_addon_tier metaobject
     *   Upsell line additionally:
     *     _bundleUpsell    → "true" (excluded from the bundle discount group)
     */
    _buildCartItems() {
      const items = [];
      const box   = String(this.boxNumber);

      // Component lines (steps 1+2)
      this.selection.forEach((qty, vid) => {
        items.push({
          id: parseInt(vid, 10),
          quantity: qty,
          properties: {
            'Box':                    box,
            '_bundleName':            this.bundleTitle,
            '_easyBundle:prodQty':    '1',
            '_easyBundle:OfferId':    this.offerId,
          },
        });
      });

      // Add-on lines
      this.addonSel.forEach(({ tierId, qty }, vid) => {
        items.push({
          id: parseInt(vid, 10),
          quantity: qty,
          properties: {
            'Box':                    box,
            '_bundleName':            this.bundleTitle,
            '_easyBundle:prodQty':    '1',
            '_easyBundle:OfferId':    this.offerId,
            '_addon_offer_id':        this.offerId,
            '_addon_product':         'true',
            '_addonTierId':           tierId,
          },
        });
      });

      // Upsell line (if selected; uses its own variant id from data attr)
      if (this.upsellSel && this.upsellBtn) {
        const upsellVid = this.upsellBtn.dataset.variantId;
        if (upsellVid) {
          items.push({
            id: parseInt(upsellVid, 10),
            quantity: 1,
            properties: {
              'Box':                  box,
              '_bundleName':          this.bundleTitle,
              '_easyBundle:prodQty':  '1',
              '_easyBundle:OfferId':  this.offerId,
              // Marks this as the optional upsell (full price). The pricing
              // function excludes _bundleUpsell lines from the bundle group so
              // it never inflates selection_total or steals the bundle discount.
              '_bundleUpsell':        'true',
            },
          });
        }
      }

      return items;
    }

    // ── Toast ─────────────────────────────────────────────────────────────

    _showToast(msg, ms) {
      if (!this.toast) return;
      this.toast.textContent = msg;
      this.toast.classList.add('bb__toast--visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.classList.remove('bb__toast--visible');
      }, ms || 3000);
    }
  }

  // ─── PDP Modal controller ────────────────────────────────────────────────

  class BundleModal {
    constructor(overlay) {
      this.overlay = overlay;
      this.modal   = overlay.querySelector('.bb-modal');
      this.closeBtn = overlay.querySelector('.bb-modal__close');
      this.builder  = null;

      const builderRoot = overlay.querySelector('.bb-modal__body .bb');
      if (builderRoot) {
        this.builder = new BundleBuilder(builderRoot);
      }

      this._bindEvents();
    }

    open() {
      this.overlay.classList.add('is-open');
      this.overlay.removeAttribute('aria-hidden');
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
      // Focus trap: move focus to close button
      if (this.closeBtn) this.closeBtn.focus();
      document.body.style.overflow = 'hidden';
      // Hides floating widgets (chat, quiz teaser) that outstack the overlay.
      document.body.classList.add('bb-modal-open');
      // The chat host pins itself with inline !important styles, so the
      // body-class CSS can't reach it — override at the same level.
      const chat = document.getElementById('livechat-widget-host');
      if (chat) chat.style.setProperty('visibility', 'hidden', 'important');
    }

    close() {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('bb-modal-open');
      const chat = document.getElementById('livechat-widget-host');
      if (chat) chat.style.removeProperty('visibility');
      // Return focus to the trigger
      if (this._trigger) {
        this._trigger.setAttribute('aria-expanded', 'false');
        this._trigger.focus();
      }
    }

    _bindEvents() {
      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Click outside modal
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
          this.close();
        }
      });

      // Focus trap within modal
      this.modal.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const focusable = Array.from(
          this.modal.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.closest('[aria-hidden="true"]'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
  }

  // ─── Init on DOMContentLoaded ────────────────────────────────────────────

  function init() {
    // Landing-page builders
    document.querySelectorAll('.bb[data-bundle-id]').forEach((root) => {
      // Skip builders inside a modal overlay — those are owned by BundleModal.
      if (root.closest('.bb-modal-overlay')) return;
      new BundleBuilder(root);
    });

    // PDP modal overlay
    const overlay = document.querySelector('.bb-modal-overlay[data-bundle-modal]');
    if (overlay) {
      // Re-parent to <body>: the overlay is rendered inside the product-info
      // column, where sticky/transform ancestors would break position:fixed
      // and overflow clipping would swallow it.
      if (overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }
      const modalCtrl = new BundleModal(overlay);

      // Wire up any "open modal" trigger buttons on the page
      document.querySelectorAll('[data-open-bundle-modal]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          modalCtrl._trigger = trigger;
          modalCtrl.open();
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
